import { TxStatusBadge } from '@/components/ui/Badge';
import { maskedCard, relativeTime, rub, usdt } from '@/lib/format';
import type { TraderTransaction } from '@/types/api';

/**
 * Recent activity. A real table on desktop so amounts stack in one column and
 * decimal points line up; stacked rows below 768px, where a five-column table
 * would either overflow or shrink the numbers to illegibility.
 *
 * «Комиссия» is the trader's own cut and the column they actually work from.
 * A server that has not shipped the field yet leaves an em dash rather than a
 * confident 0,00 — a trader told they earned nothing on a settled payment will
 * open a ticket, and rightly.
 */
export function ActivityList({ transactions }: { transactions: TraderTransaction[] }) {
  return (
    <>
      {/* Desktop: aligned columns. The scroller keeps an eight-column table from
          pushing the page sideways between 768px and roughly 1200px. */}
      <div className="hidden overflow-x-auto md:block">
      {/* 54rem rather than 48: the commission column joined the row, and the
          Russian status badges and «назад» timestamps are wider than the
          English they replaced. */}
      <table className="w-full min-w-[54rem]">
        <caption className="sr-only">Десять последних транзакций</caption>
        <thead>
          <tr className="border-b border-line">
            <th scope="col" className="eyebrow whitespace-nowrap px-4 py-2 text-left font-semibold">
              Мерчант
            </th>
            <th scope="col" className="eyebrow whitespace-nowrap px-4 py-2 text-left font-semibold">
              Номер
            </th>
            <th scope="col" className="eyebrow whitespace-nowrap px-4 py-2 text-left font-semibold">
              Карта
            </th>
            <th scope="col" className="eyebrow whitespace-nowrap px-4 py-2 text-right font-semibold">
              Сумма
            </th>
            <th scope="col" className="eyebrow whitespace-nowrap px-4 py-2 text-right font-semibold">
              USDT
            </th>
            <th scope="col" className="eyebrow whitespace-nowrap px-4 py-2 text-right font-semibold">
              Ваша комиссия
            </th>
            <th scope="col" className="eyebrow whitespace-nowrap px-4 py-2 text-left font-semibold">
              Статус
            </th>
            <th scope="col" className="eyebrow whitespace-nowrap px-4 py-2 text-right font-semibold">
              Когда
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {transactions.map((tx) => (
            <tr key={tx.id} className="row-tight transition-colors duration-fast hover:bg-sunken">
              <td className="max-w-[14rem] truncate px-4 text-sm text-fg" title={tx.merchantName}>
                {tx.merchantName}
              </td>
              <td
                className="num max-w-[12rem] truncate px-4 text-xs text-fg-muted"
                title={tx.reference}
              >
                {tx.reference}
              </td>
              <td className="num whitespace-nowrap px-4 text-xs text-fg-secondary">
                {tx.cardBank ? `${tx.cardBank} ` : ''}
                {maskedCard(tx.cardLast4)}
              </td>
              <td className="num whitespace-nowrap px-4 text-right text-sm font-medium text-fg">
                {rub(tx.amountRub)}
              </td>
              <td className="num whitespace-nowrap px-4 text-right text-sm text-fg-secondary">
                {usdt(tx.amountUsdt)}
              </td>
              <td className="num whitespace-nowrap px-4 text-right text-sm font-medium text-fg">
                {tx.traderMarkupRub != null ? rub(tx.traderMarkupRub) : tx.commissionUsdt === undefined ? '—' : usdt(tx.commissionUsdt)}
              </td>
              <td className="px-4">
                <TxStatusBadge status={tx.status} />
              </td>
              <td className="whitespace-nowrap px-4 text-right text-xs text-fg-muted">
                <time dateTime={tx.createdAt}>{relativeTime(tx.createdAt)}</time>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      {/* Narrow: stacked rows, amount still leading */}
      <ul className="divide-y divide-line md:hidden">
        {transactions.map((tx) => (
          <li key={tx.id} className="px-4 py-3">
            <div className="flex items-baseline justify-between gap-3">
              <span className="num text-md font-medium text-fg">{rub(tx.amountRub)}</span>
              <TxStatusBadge status={tx.status} />
            </div>
            <p className="mt-1 truncate text-sm text-fg-secondary">{tx.merchantName}</p>
            {tx.commissionUsdt !== undefined ? (
              <p className="mt-1 text-xs text-fg">
                Ваша комиссия <span className="num font-medium">{tx.traderMarkupRub != null ? rub(tx.traderMarkupRub) : usdt(tx.commissionUsdt)}</span>
              </p>
            ) : null}
            <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-xs text-fg-muted">
              <span className="num">{maskedCard(tx.cardLast4)}</span>
              <span className="num">{usdt(tx.amountUsdt)}</span>
              <time dateTime={tx.createdAt}>{relativeTime(tx.createdAt)}</time>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

